import React, { useRef, useState } from "react";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useNavigate, useLocation } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import { dialCodeOptions, getFlagEmoji } from "@/utils/countryCodes";
import { isValidEmail, isValidMobile } from "@/utils";
import { membersService } from "@/services/members.service";
import MDialCode from "@/components/ui/molecules/MDialCode";
import MInputField from "@/components/ui/atoms/MInputField";
import MRadio from "@/components/ui/atoms/MRadio";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { fetchNickname } from "@/hooks/useMemberPermission";

import { getMemberInfo } from "@/hooks/useMembers";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { sanitizedEmail } from "@/utils/member";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const InviteContactMethod: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const member = location.state?.targetMember || {};
  const backUrl = location.state?.backUrl;
  const memberInfo = getMemberInfo(true);
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [mobileValue, setMobileValue] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [isContactInputFocused, setIsContactInputFocused] = useState(false);
  const blurTimeoutRef = useRef<number | undefined>(undefined);

  React.useEffect(() => {
    async function fetchMemberData() {
      const memberId = member?._id || member?.id;
      if (!memberId) return;

      // Fetch member name/nickname from profile
      const fetched = await membersService.getMember(memberId);
      setName(fetched.first_name || "");
      fetchNickname({ familyMemberId: fetched._id }).then((nick) =>
        setNickname(nick || "")
      );

      // Pre-populate contact fields from the invite record (not the member profile).
      // Unregistered members have no contact on their profile — it lives on the invite.
      const familyId = memberInfo?.current_family || "";
      const invite = await membersService.getMemberInvite(memberId, familyId);
      if (invite) {
        setMobileValue(invite.mobile || "");
        setEmailValue(invite.email || "");
      }
    }
    fetchMemberData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  // UI State
  const [view, setView] = useState<"select" | "mobile" | "email">("select");
  const [selected, setSelected] = useState<"mobile" | "email" | null>(null);
  const [dialCodeDrawerOpen, setDialCodeDrawerOpen] = useState(false);
  const [inputError, setInputError] = useState(""); // Only for validation, not backend errors
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_INVITE_AGAIN,
    });
  }, [memberInfo?.id]);

  const handleContactFocus = () => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = undefined;
    }
    setIsContactInputFocused(true);
  };

  const handleContactBlur = () => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsContactInputFocused(false);
      blurTimeoutRef.current = undefined;
    }, 150);
  };

  // Handle Send Invite directly
  const handleSendInvite = async () => {
    const memberId = member.id || member._id;
    if (!memberId) return;
    setLoading(true);
    try {
      if (selected === "mobile") {
        const cleanMobile = mobileValue.replace(/\D/g, "");
        if (!/^\d{10,15}$/.test(cleanMobile)) {
          setInputError("Enter a valid mobile number");
          setLoading(false);
          return;
        }
        // Contact is sent with the invite — not stored on the member profile
        await membersService.inviteMember(memberId, "mobile", {
          mobile: cleanMobile,
          dialCode: dialCode.replace(/^\+/, ""),
        });
        navigate("/profile/family/invite-success", {
          state: {
            memberName: name,
            memberNickname: nickname,
            backUrl,
            memberId,
          },
        });
      } else if (selected === "email") {
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailValue)) {
          setInputError("Enter a valid email address");
          setLoading(false);
          return;
        }
        await membersService.inviteMember(memberId, "email", {
          email: sanitizedEmail(emailValue),
        });
        navigate("/profile/family/invite-success", {
          state: {
            memberName: name,
            memberNickname: nickname,
            backUrl,
            memberId,
          },
        });
      }
    } catch (e: any) {
      let errorMsg = "Failed to update or invite. Please try again.";

      // Handle nested error structure: e.response.data.message can be an object with {message, error, statusCode}
      if (e?.response?.data?.message) {
        const apiMessage = e.response.data.message;
        if (typeof apiMessage === "string") {
          errorMsg = apiMessage;
        } else if (
          apiMessage?.message &&
          typeof apiMessage.message === "string"
        ) {
          errorMsg = apiMessage.message;
        }
      } else if (e?.message?.message && typeof e.message.message === "string") {
        errorMsg = e.message.message;
      } else if (e?.message && typeof e.message === "string") {
        errorMsg = e.message;
      }

      toast?.showToast({ message: errorMsg, variant: "error" });
      setLoading(false);
    }
  };

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Family"
        memberInfo={member}
        title={`Send invite to ${
          member.nickname || member.first_name || "Member"
        }`}
        description="We will send them an invite to join your family on their mobile number or email."
        hideLearnMore
      />
      <MSpacer mt={52} />
      <div className="body-base-medium header-color">
        Select how you would like to invite them.
      </div>
      <div className="pb-32">
        {view === "select" && (
          <>
            <div className="flex flex-col gap-[32px] mt-[32px]">
              <div
                className="add-member-label"
                style={{ position: "relative", left: 30, top: 15 }}
              >
                Mobile Number
              </div>
              <div className="flex items-center gap-2">
                <MRadio
                  id="radio-mobile"
                  name="invite-method"
                  checked={selected === "mobile"}
                  onChange={() => setSelected("mobile")}
                  size="sm"
                />
                <div className="flex items-center gap-2 w-full">
                  <span style={{ position: "relative", width: 60 }}>
                    <MInputField
                      size="md"
                      variant="Underlined"
                      showLabel={true}
                      value={(() => {
                        return dialCode;
                      })()}
                      disabled
                      className="cursor-pointer text-center min-w-[90px]"
                      onClick={() => setDialCodeDrawerOpen(true)}
                      readOnly
                      style={{ minWidth: 0, width: 54 }}
                    />
                  </span>
                  <span
                    style={{ position: "relative", left: 3, width: "100%" }}
                  >
                    <MInputField
                      size="md"
                      variant="Underlined"
                      state="Default"
                      type="tel"
                      inputMode="numeric"
                      placeholder="Not Answered"
                      value={mobileValue}
                      onChange={(e) =>
                        setMobileValue(e.target.value.replace(/\D/g, ""))
                      }
                      onFocus={() => {
                        setSelected("mobile");
                        handleContactFocus();
                      }}
                      onClick={() => setSelected("mobile")}
                      onBlur={handleContactBlur}
                      className="flex-1 w-full"
                    />
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MRadio
                  id="radio-email"
                  name="invite-method"
                  checked={selected === "email"}
                  onChange={() => setSelected("email")}
                  size="sm"
                />
                <div className="flex items-center gap-2 w-full">
                  <MInputField
                    label="Email"
                    placeholder="Not Answered"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    type="email"
                    showLabel={true}
                    size="md"
                    variant="Underlined"
                    onFocus={() => {
                      setSelected("email");
                      handleContactFocus();
                    }}
                    onClick={() => setSelected("email")}
                    onBlur={handleContactBlur}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {view === "mobile" && (
          <>
            <div className="flex flex-row items-center gap-3 w-full max-w-md mt-6">
              <div
                className="auth-dialcode-box flex items-center justify-center"
                style={{
                  boxSizing: "border-box",
                  width: 93,
                  height: 67,
                  marginTop: -12,
                  border: "1px solid #E1E4EA",
                  borderRadius: 12,
                  background: "#F9F9FB",
                }}
              >
                <span style={{ fontSize: 28, marginRight: 6 }}>
                  {(() => {
                    const codeObj = dialCodeOptions.find(
                      (opt) => opt.value === dialCode
                    );
                    return codeObj ? getFlagEmoji(codeObj.countryCode) : "";
                  })()}
                </span>
                <span>{dialCode}</span>
              </div>
              <MInputField
                size="xl"
                variant="Outlined"
                state="Default"
                type="tel"
                label="Mobile Number"
                placeholder="Mobile number"
                value={mobileValue}
                onChange={(e) =>
                  setMobileValue(e.target.value.replace(/\D/g, ""))
                }
                className="flex-1 h-[75px]"
                showLabel={true}
                autoFocus
                onFocus={handleContactFocus}
                onBlur={handleContactBlur}
              />
            </div>
            <div className="w-full max-w-md flex justify-end mt-2">
              <MButton
                size="sm"
                variant="link"
                onClick={() => setView("select")}
              >
                Cancel
              </MButton>
            </div>
            <MDialCode
              open={dialCodeDrawerOpen}
              onClose={() => setDialCodeDrawerOpen(false)}
              onDone={(code) => {
                setDialCode(code);
                setDialCodeDrawerOpen(false);
              }}
              initialValue={dialCode}
              title="Select Country Code"
            />
          </>
        )}
        {view === "email" && (
          <>
            <div className="w-full max-w-md mt-6">
              <MInputField
                label="Email Address"
                placeholder="Email address"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                type="email"
                showLabel={true}
                autoFocus
                variant="Outlined"
                onFocus={handleContactFocus}
                onBlur={handleContactBlur}
              />
            </div>
            <div className="w-full max-w-md flex justify-end mt-2">
              <MButton
                size="sm"
                variant="link"
                onClick={() => setView("select")}
              >
                Cancel
              </MButton>
            </div>
          </>
        )}
        {/* Send Invite button at bottom, visible in all views */}
        {!isContactInputFocused && (
          <div
            className="w-full max-w-md fixed left-1/2 -translate-x-1/2 px-4 py-4 bg-white z-10"
            style={{ bottom: "env(safe-area-inset-bottom)" }}
          >
            <MButton
              size="lg"
              variant="filled"
              className="w-full"
              onClick={handleSendInvite}
              disabled={
                loading ||
                !selected ||
                (selected === "mobile" &&
                  (!mobileValue || !isValidMobile(mobileValue))) ||
                (selected === "email" &&
                  (!emailValue || !isValidEmail(emailValue)))
              }
            >
              {loading ? "Sending..." : "Send Invite"}
            </MButton>
            {inputError && (
              <div className="text-red-500 text-sm mt-2 text-center">
                {inputError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteContactMethod;
